import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Sidebar from '../components/Sidebar';
import TopButtons from '../components/common/TopButtons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useChainId } from 'wagmi';
import { ethers } from 'ethers'; //
import CryptoJS from 'crypto-js'; 

import { useMemo } from 'react'
import { type Config, useConnectorClient } from 'wagmi'
import { clientToSigner } from '../utils/provider'; // 
import CustomModal from '../components/common/CustomModal'; 
import Loading from '../components/common/Loading'; 

import { useAnalytics } from '../hooks/useAnalytics';
import { getFactoryAddress, getInvitationAddress, getNativeToken } from '../config/contracts';
import GameList from '../components/GameList';
import { useWalletTab } from '../context/WalletTabContext';
import { useToast } from '../contexts/ToastContext';

/** Hook to convert a viem Wallet Client to an ethers.js Signer. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
  const { data: client } = useConnectorClient<Config>({ chainId })
  return useMemo(() => (client ? clientToSigner(client) : undefined), [client])
}



const SALT = 'ethereum_address_salt'; 

const formatAddress = (address: string) => {
  if (address.length <= 10) return address;
  return `${address.slice(0, 10)}...${address.slice(-10)}`;
};

const HomeTopButtons = styled(TopButtons)`
`;

const WalletContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0a1a;
  color: white;
  padding: 10px 10px;
  margin-left: 240px;
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding-bottom: 80px;
  }
`;

const ContentWrapper = styled.div`

  margin: 10px 10px;
  width: 100%;
`;

const WalletSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;
const TabWalletSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 20px;
`;

const Label = styled.span`
  display: block;
  color: #4ad0ac;
  margin-bottom: 8px;
  font-size: 14px;
`;

const Value = styled.p`
  color: white;
  font-size: 16px;
  margin-bottom: 16px;
  word-break: break-all;
`;

const CopyLink = styled.span`
  color: #4ad0ac;;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ShareButton = styled.button`
  background: none;
  border: 1px solid #4ad0ac;
  color: #4ad0ac;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(74, 208, 172, 0.1);
  }
`;

const BindButton = styled.button`
  background: #4ad0ac;
  border: none;
  border-radius: 8px;
  color: #0a0a1a;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #3ab090;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
  margin-top: 20px;
  
  th {
    color: #4ad0ac;
    font-weight: normal;
    padding: 12px;
    text-align: left;
    font-size: 14px;
  }
`;

const TableBody = styled.tbody`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TableRow = styled.tr`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: flex;
  
  td {
    padding: 16px;
    color: rgba(255, 255, 255);
    font-size: 14px;
    border: none;
    width: 100%;
    border-radius: 8px;
  }
`;

const ModalInput = styled.input`
  width: 95%;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  margin-right: -10px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #4ad0ac;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 4px;
  margin-left: 15px;
  margin-top: 15px;
  width: fit-content;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? '#4ad0ac' : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${props => props.isActive ? '#0a0a1a' : '#4ad0ac'};
  padding: 8px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: ${props => props.isActive ? '#4ad0ac' : 'rgba(74, 208, 172, 0.1)'};
  }
`;

const RewardsText = styled.div`
  color: #4ad0ac;
  font-size: 14px;
  margin: 20px 0;
`;

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTab, setActiveTab } = useWalletTab();
  const { isConnected, address } = useAccount();
  const chainId = useChainId();
  const nativeToken = getNativeToken(chainId);
  const { sendEvent } = useAnalytics();
  const signer = useEthersSigner();
  const [inviterAddress, setInviterAddress] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [isLoading, setIsLoading] = useState(false); 
  const [inviterInput, setInviterInput] = useState('');
  const [isInviterBound, setIsInviterBound] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inviterList, setInviterList] = useState<string[]>([]);
  const [encryptedAddress, setEncryptedAddress] = useState('');
  const [totalReward, setTotalReward] = useState<string>('0');
  const { showToast } = useToast();

  const inviteLinkPrefix = "Click to copy the invitation link:";
  const inviteErr ="Failed to bind inviter, please check the invite code or try again";
  const newInviteLink = `https://yolopad.fun/mywallet?refer=${encodeURIComponent(encryptedAddress)}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newInviteLink).then(() => {
      showToast(t('mywallet.toast.inviteLinkCopied'), 'success');
    });
    sendEvent('button_click', {
      button_name: 'copy_invite_link_button',
      page_location: 'subscription_page'
    });
  };

  const handleBindInviterClick = () => {
    setIsModalOpen(true);
    sendEvent('button_click', {
      button_name: 'bind_inviter_button',
      page_location: 'subscription_page'
    });
  };

  const handleModalClose = () => {
    setInviterInput(''); // 
    setIsModalOpen(false); // 
    setErrorMessage(''); // 
  };
  
  const fetchUserTotalReward = async () => {
    if (isConnected && address && signer) {
      try {
        const contract = new ethers.Contract(
          getFactoryAddress(chainId),
          ['function userTotalRewardRefer(address _invitee) external view returns (uint256)'],
          signer
        );
        const reward = await contract.userTotalRewardRefer(address);
        setTotalReward(ethers.formatEther(reward));
      } catch (error) {
        console.error('Error fetching total reward:', error);
        setTotalReward('0');
      }
    }
  };
  const fetchInviterAddress = async () => {
    if (isConnected && address && signer) {
      const contract = new ethers.Contract(
        getInvitationAddress(chainId),
        ['function getCurrentInviter(address _user) external view returns (address)'],
        signer
      );

      const inviter = await contract.getCurrentInviter(address);
      setInviterAddress(inviter);
       const urlParams = new URLSearchParams(window.location.search);
      const referAddressParam = urlParams.get('refer');
      const referAddress = referAddressParam ? decodeURIComponent(referAddressParam) : null; //
      if (referAddress && !isInviterBound) {
        if (!inviter || inviter === '0x0000000000000000000000000000000000000000') {
           setInviterInput(referAddress); 
           setIsModalOpen(true);
        
          }
       }else{
        console.log('referAddress', 'referAddress');
       }
    }
  };

  const fetchInviterList = async () => {
    if (isConnected && address && signer) {
      try {
        const contract = new ethers.Contract(
          getInvitationAddress(chainId),
          ['function getInviteInfo(address _invitee) external view returns (address[] memory)'],
          signer
        );

        const inviteInfo = await contract.getInviteInfo(address);
        setInviterList(inviteInfo); 
      } catch (error) {
        console.error('Error fetching inviter list:', error);
      }
    }
  };

  useEffect(() => {
    if (address) {
      const encrypted = CryptoJS.AES.encrypt(address, SALT).toString();
      setEncryptedAddress(encrypted);
    } else {
      setEncryptedAddress('');
    }
  }, [address]);

  useEffect(() => {
    if (!chainId || !isConnected || !address) return;
    fetchInviterAddress();
    fetchUserTotalReward();
    fetchInviterList();
  }, [isConnected, address, signer, chainId]);

  const handleConfirm = async () => {
    try {
      if (!signer) {
        throw new Error("Provider is not available");
      }

      let decryptedAddress: string;
      if (ethers.isAddress(inviterInput)) {
        decryptedAddress = inviterInput;
      } else {
        const decryptedBytes = CryptoJS.AES.decrypt(inviterInput, SALT);
        decryptedAddress = decryptedBytes.toString(CryptoJS.enc.Utf8);
      }

      if (!ethers.isAddress(decryptedAddress)) {
        showToast(t('mywallet.modal.invalidCode'), 'error');
        return; 
      }
      setIsLoading(true)

      const contract = new ethers.Contract(
        getInvitationAddress(chainId),
        ['function bindInvitation(address _inviter) external'],
        signer
      );

      const tx = await contract.bindInvitation(decryptedAddress);
      await tx.wait();

      setIsInviterBound(true);
      setInviterInput('')
      setIsModalOpen(false);
      fetchInviterAddress();
      fetchInviterList();
      setErrorMessage('');
      sendEvent('button_click', {
        button_name: 'bind_inviter_button',
        page_location: 'mywallet_page'
      });
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error('Error binding inviter:', error);
      setErrorMessage(t('mywallet.modal.bindFailed'));
    }
  };

  const shareMessage = `https://x.com/intent/tweet?text=${encodeURIComponent(`Get in before the burn starts.I'm already minting.  @Yolopadfun is live — join now 👇: ${newInviteLink}`)} \n`
  const handleShareClick = () => {
    sendEvent('share_click', {
      share_platform: 'twitter',
      share_location: 'subscription_page'
    });
    
    window.open(
        shareMessage,
      '_blank'
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'invitation':
        return (
          <>
            {inviterList.length === 0 ? (
              <Value>{t('mywallet.noData')}</Value>
            ) : (
              <Table>
                <TableBody>
                  {inviterList.map((inviter, index) => (
                    <TableRow key={index}>
                      <td>{formatAddress(inviter)}</td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        );
        case 'create':
          return <GameList creatorAddress={address || ''} />;
        case 'favorite':
          return <GameList isFavorite={true} />;
    }
  };

  const handleConnectWallet = () => {
  };

  const handleBuyToken = () => {
    navigate('/create');
  };

  return (
    <>
      <Sidebar />
      <WalletContainer>
        <HomeTopButtons 
          onFirstButtonClick={handleConnectWallet}
          onSecondButtonClick={handleBuyToken}
        />
        {isConnected ? (
        <ContentWrapper>
          <WalletSection>
            <Label>{t('mywallet.walletAddress')}</Label>
            <Value>{address}</Value>
            <Label>{t('mywallet.invitedRewards')} {totalReward} {nativeToken}</Label>
            
            <Label>{t('mywallet.inviter')}</Label>
            {inviterAddress === '0x0000000000000000000000000000000000000000' && !isInviterBound && (
              <ShareButton onClick={handleBindInviterClick}>
                {t('mywallet.bindInviter')}
              </ShareButton>
            )}
            <Value>
              {inviterAddress === '0x0000000000000000000000000000000000000000' 
                ? '' 
                : inviterAddress ? formatAddress(inviterAddress) : '--'}
            </Value>
            
            <Label>{t('mywallet.inviteLinkPrefix')}</Label>
            <Value>
              <CopyLink onClick={copyToClipboard}>
                {newInviteLink}
              </CopyLink>
            </Value>
            <Value style={{ color: 'rgba(255, 255, 255)' }}>
              {t('mywallet.shareMessage')}
            </Value>
            
            <ShareButton onClick={handleShareClick}>
              {t('mywallet.shareToX')}
            </ShareButton>
          </WalletSection>

          <TabWalletSection>
            <TabContainer>
              <TabButton 
                isActive={activeTab === 'invitation'} 
                onClick={() => setActiveTab('invitation')}
              >
                {t('mywallet.tabs.invitationList')}
              </TabButton>
              <TabButton 
                isActive={activeTab === 'create'} 
                onClick={() => setActiveTab('create')}
              >
                {t('mywallet.tabs.myCreate')}
              </TabButton>
              <TabButton 
                isActive={activeTab === 'favorite'} 
                onClick={() => setActiveTab('favorite')}
              >
                {t('mywallet.tabs.myFavorite')}
              </TabButton>
            </TabContainer>
            {renderContent()}
          </TabWalletSection>
        </ContentWrapper>
       ) : (
        <ContentWrapper style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <ConnectButton />
        </ContentWrapper>
        )}
        {isModalOpen && (
          <CustomModal onClose={handleModalClose} onConfirm={handleConfirm} errorMessage={errorMessage}>
            <ModalInput 
              type="text" 
              value={inviterInput} 
              onChange={(e) => setInviterInput(e.target.value)}
              placeholder={t('mywallet.modal.enterInviteCode')}
              className="border p-2 w-full mb-4 text-white placeholder-white bg-transparent"
            />
          </CustomModal>
        )}
        {isLoading && <Loading />}
      </WalletContainer>
    </>
  );
};

export default Home; 